import React, { useState, useEffect, useRef } from 'react';
import { Table, Form, Container, Tabs, Tab, Modal, Button, InputGroup, FormControl, Dropdown } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { format, parseISO, differenceInDays } from 'date-fns';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { renderAsync } from 'docx-preview';

const Engagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { engagements, updateEngagement } = useAppContext();
  
  // Ref for preview container
  const previewContainerRef = useRef<HTMLDivElement>(null);
  
  // State for editing
  const [editingEngagement, setEditingEngagement] = useState<any | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);

  // State for pagination
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [accountingCurrentPage, setAccountingCurrentPage] = useState(1);
  const [secretarialCurrentPage, setSecretarialCurrentPage] = useState(1);
  const itemsPerPage = 10; // Show 10 items per page

  // State for bank confirmation popup
  const [showBankConfModal, setShowBankConfModal] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState<any>(null);
  const [useSystemData, setUseSystemData] = useState<boolean | null>(null);
  const [bankName, setBankName] = useState('');
  const [bankAddress, setBankAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientBankAccountNumber, setClientBankAccountNumber] = useState('');
  const [yearEndDate, setYearEndDate] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [bankData, setBankData] = useState<any[]>([]);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // State for sorting
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // State for PDF upload
  const [showPdfUploadModal, setShowPdfUploadModal] = useState(false);
  const [currentEngagement, setCurrentEngagement] = useState<any>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  // State for status modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [currentStatusEngagement, setCurrentStatusEngagement] = useState<any>(null);
  const [currentStatus, setCurrentStatus] = useState('');
  const [statusHistory, setStatusHistory] = useState<any[]>([]);

  // State for Excel export
  const [activeTabForExport, setActiveTabForExport] = useState('audit');

  // State for 18-month filter
  const [show18MonthFilter, setShow18MonthFilter] = useState(false);

  // State for services modal
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [currentServicesEngagement, setCurrentServicesEngagement] = useState<any>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // Handle edit field
  const handleEditField = (engagementId: number, field: string, value: any) => {
    updateEngagement(engagementId, field as keyof typeof engagements[0], value);
  };

  // Start editing a field
  const startEditing = (engagement: any, field: string) => {
    setEditingEngagement(engagement);
    setEditingField(field);
  };

  // Stop editing
  const stopEditing = () => {
    setEditingEngagement(null);
    setEditingField(null);
  };

  // Handle status click
  const handleStatusClick = (engagement: any) => {
    setCurrentStatusEngagement(engagement);
    setCurrentStatus(engagement.status);

    // Generate mock status history data based on engagement type
    let mockHistory: any[] = [];
    if (engagement.engagementCode.startsWith('AUD')) {
      // Audit Status
      mockHistory = [
        { date: '2026-01-01', status: 'Audit-in-charge start', approvedBy: 'Alice Johnson' },
        { date: '2026-01-02', status: 'Manager review', approvedBy: 'Bob Williams' },
        { date: '2026-01-03', status: 'Partner review', approvedBy: 'Jacky' },
      ];
    } else if (engagement.engagementCode.startsWith('ACC')) {
      // Account Status
      mockHistory = [
        { date: '2026-01-01', status: 'document received', approvedBy: 'Carol Davis' },
        { date: '2026-01-02', status: 'Acct start', approvedBy: 'Carol Davis' },
      ];
    } else {
      // Company Secretary Status
      mockHistory = [
        { date: '2026-01-01', status: 'document preparing', approvedBy: 'David Wilson' },
        { date: '2026-01-02', status: 'document sent to client to sign', approvedBy: 'David Wilson' },
      ];
    }

    setStatusHistory(mockHistory);
    setShowStatusModal(true);
  };

  // Handle status change in modal
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    setCurrentStatus(newStatus);
  };

  // Handle save status
  const handleSaveStatus = () => {
    if (currentStatusEngagement) {
      updateEngagement(currentStatusEngagement.id, 'status', currentStatus);

      // Add to status history
      const newHistoryEntry = {
        date: new Date().toISOString().split('T')[0],
        status: currentStatus,
        approvedBy: 'Current User' // In a real app, this would be the current user
      };

      setStatusHistory(prev => [...prev, newHistoryEntry]);
    }
    setShowStatusModal(false);
  };

  // Handle cancel status
  const handleCancelStatus = () => {
    setShowStatusModal(false);
    setCurrentStatusEngagement(null);
    setCurrentStatus('');
  };

  // Handle 18-month filter
  const handle18MonthFilter = () => {
    setShow18MonthFilter(!show18MonthFilter);
  };

  // Handle services click
  const handleServicesClick = (engagement: any) => {
    setCurrentServicesEngagement(engagement);
    setSelectedServices([...engagement.services]);
    setShowServicesModal(true);
  };

  // Handle service selection
  const handleServiceSelection = (service: string) => {
    setSelectedServices(prev => {
      if (prev.includes(service)) {
        return prev.filter(s => s !== service);
      } else {
        return [...prev, service];
      }
    });
  };

  // Handle save services
  const handleSaveServices = () => {
    if (currentServicesEngagement) {
      updateEngagement(currentServicesEngagement.id, 'services', selectedServices);
    }
    setShowServicesModal(false);
    setCurrentServicesEngagement(null);
    setSelectedServices([]);
  };

  // Handle cancel services
  const handleCancelServices = () => {
    setShowServicesModal(false);
    setCurrentServicesEngagement(null);
    setSelectedServices([]);
  };

  // Handle bank confirmation click
  const handleBankConfClick = async (engagement: any) => {
    setSelectedEngagement(engagement);
    setUseSystemData(null);
    setBankName('');
    setBankAddress('');
    setClientName('');
    setClientBankAccountNumber('');
    setYearEndDate(engagement.yearEndDate || '');

    // Load bank data when opening the modal
    try {
      const response = await fetch('/hongkongbank.csv');
      const csvText = await response.text();
      const results: any[] = Papa.parse(csvText, { header: true }).data;
      setBankData(results);
    } catch (error) {
      console.error('Error loading bank data:', error);
    }

    setShowBankConfModal(true);
  };

  // Handle Yes/No selection
  const handleYesClick = () => {
    setUseSystemData(true);
    setClientName(selectedEngagement?.companyName || '');
  };

  const handleNoClick = () => {
    setUseSystemData(false);
  };

  // Search banks from CSV
  const searchBanks = (query: string) => {
    setIsSearching(true);
    fetch('/hongkongbank.csv')
      .then(response => response.text())
      .then(csvText => {
        const results: any[] = Papa.parse(csvText, { header: true }).data;
        setBankData(results); // Store the bank data for later use

        const filteredResults = results.filter((bank: any) => {
          const searchLower = query.toLowerCase().trim();
          if (!searchLower) return false;

          // Check if any of the fields contain the search query (case-insensitive)
          return (
            (bank['ABBR'] && bank['ABBR'].toString().toLowerCase().includes(searchLower)) ||
            (bank['Institution Name'] && bank['Institution Name'].toString().toLowerCase().includes(searchLower)) ||
            (bank['Clearing Code'] && bank['Clearing Code'].toString().toLowerCase().includes(searchLower)) ||
            (bank['Branch Name'] && bank['Branch Name'].toString().toLowerCase().includes(searchLower)) ||
            (bank['Branch Code'] && bank['Branch Code'].toString().toLowerCase().includes(searchLower))
          );
        });
        setSearchResults(filteredResults);
        setShowSearchResults(true);
      })
      .catch(error => {
        console.error('Error loading bank data:', error);
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  // Handle search input change with debounce
  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        searchBanks(searchQuery);
      }, 300); // 300ms delay
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle bank selection from search results
  const handleBankSelect = (bank: any) => {
    setBankName(bank['Institution Name']);
    setBankAddress(bank['Branch Address']);
    setSearchQuery(''); // Clear the search query after selection
    setShowSearchResults(false);
  };

  // Handle preview click
  const handlePreviewClick = async () => {
    // 验证必要字段
    if (!bankName || !bankAddress) {
      alert('请先选择银行并填写银行地址。');
      return;
    }

    setIsGeneratingPreview(true);
    setPreviewError(null);
    setShowPreview(true); // 先显示模态框

    const currentYear = new Date().getFullYear().toString();
    const partnerInfo = selectedEngagement?.partner || '';
    const clientCode = selectedEngagement?.auditClientCode || selectedEngagement?.accountingClientCode || selectedEngagement?.companySecretarialCode || '';
    const companyName = selectedEngagement?.companyName || '';

    try {
      // 清空预览容器
      if (previewContainerRef.current) {
        previewContainerRef.current.innerHTML = '<div style="text-align: center; padding: 50px;"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div><p style="margin-top: 20px;">正在生成preview...</p></div>';
      }

      // Generate the filled document using the template
      const blob = await loadAndFillTemplate(
        partnerInfo,
        clientCode,
        currentYear,
        companyName,
        bankName,
        bankAddress,
        '',
        clientBankAccountNumber
      );

      // Convert blob to ArrayBuffer for docx-preview
      const arrayBuffer = await blob.arrayBuffer();

      // Render the document in the preview container
      if (previewContainerRef.current) {
        previewContainerRef.current.innerHTML = '';
        await renderAsync(
          arrayBuffer,
          previewContainerRef.current,
          undefined,
          {
            className: 'docx-preview',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            ignoreLastRenderedPageBreak: true,
            experimental: false,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            renderEndnotes: true,
            useBase64URL: false,
            debug: false,
          }
        );
      }

      setIsGeneratingPreview(false);
    } catch (error) {
      console.error('Error generating preview:', error);
      setPreviewError('preview生成失败：' + (error instanceof Error ? error.message : '未知错误'));
      setIsGeneratingPreview(false);
      
      // 在预览容器中显示错误信息
      if (previewContainerRef.current) {
        previewContainerRef.current.innerHTML = `<div style="text-align: center; padding: 50px; color: red;"><p><strong>preview生成失败</strong></p><p>${error instanceof Error ? error.message : '未知错误'}</p><p style="margin-top: 20px;">请检查模板文件和数据是否正确。</p></div>`;
      }
    }
  };

  // Function to load and fill the template with data
  const loadAndFillTemplate = async (
    partner: string,
    clientCode: string,
    currentYear: string,
    companyName: string,
    bankName: string,
    bankAddress: string,
    telephoneNumber: string,
    accountNumber: string
  ): Promise<Blob> => {
    try {
      console.log('Loading template...');
      // Load the template file
      const response = await fetch('/Bank conf template.docx');
      
      if (!response.ok) {
        throw new Error(`模板文件加载失败: ${response.status} ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      console.log('Template loaded, size:', arrayBuffer.byteLength, 'bytes');
      
      // Unzip the content
      const zip = new PizZip(arrayBuffer);
      
      // Create the docxtemplater instance
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });
      
      // Set the template data
      const templateData = {
        partner: partner || '',
        clientCode: clientCode || '',
        currentYear: currentYear || '',
        companyName: companyName || '',
        bankName: bankName || '',
        bankAddress: bankAddress || '',
        telephoneNumber: telephoneNumber || '',
        accountNumber: accountNumber || '',
      };
      
      console.log('Rendering template with data:', templateData);
      doc.render(templateData);
      
      // Generate the output
      const blob = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      
      console.log('Template rendered successfully, blob size:', blob.size, 'bytes');
      return blob;
    } catch (error) {
      console.error('Error in loadAndFillTemplate:', error);
      if (error instanceof Error && error.message.includes('template rendering')) {
        throw new Error('模板渲染失败：请检查模板中的变量名称是否正确');
      }
      throw error;
    }
  };

  // Function to get bank telephone number from CSV
  const getBankTelephoneNumber = async (bankName: string) => {
    try {
      // Load the CSV file and find the matching bank
      const response = await fetch('/hongkongbank.csv');
      const csvText = await response.text();
      const results: any[] = Papa.parse(csvText, { header: true }).data;
      const bank = results.find((b: any) => b['Institution Name'] === bankName);
      return bank ? bank['Telephone Number'] : '';
    } catch (error) {
      console.error('Error loading bank data:', error);
      return '';
    }
  };

  // Handle export as Word
  const handleExportWord = async () => {
    // 验证必要字段
    if (!bankName || !bankAddress) {
      alert('请先选择银行并填写银行地址。');
      return;
    }

    // Get the current year
    const currentYear = new Date().getFullYear().toString();

    // Get partner info from selected engagement
    const partnerInfo = selectedEngagement?.partner || '';
    const clientCode = selectedEngagement?.auditClientCode || selectedEngagement?.accountingClientCode || selectedEngagement?.companySecretarialCode || '';
    const companyName = selectedEngagement?.companyName || '';

    try {
      // Generate the filled document using the template
      const blob = await loadAndFillTemplate(
        partnerInfo,
        clientCode,
        currentYear,
        companyName,
        bankName,
        bankAddress,
        '',
        clientBankAccountNumber
      );

      // Generate filename based on bank abbreviation and date
      const bankAbbr = bankName.substring(0, 3).toUpperCase();
      const formattedDate = yearEndDate.replace(/\//g, '').replace(/-/g, '');
      const filename = `${bankAbbr} - ${formattedDate} - Bank Confirmation.docx`;

      // Download the file
      saveAs(blob, filename);

      // Close the preview and show the confirmation
      setShowPreview(false);

      // Update the bankConf field in the engagement - add to array instead of replacing
      if (selectedEngagement) {
        const newBankConfValue = `${bankAbbr} - ${formattedDate} - prepared`;
        const existingBankConfs = Array.isArray(selectedEngagement.bankConf) ? selectedEngagement.bankConf :
                                 (selectedEngagement.bankConf && selectedEngagement.bankConf !== 'No' &&
                                  selectedEngagement.bankConf !== 'Pending' && selectedEngagement.bankConf !== 'TBD' ?
                                  [selectedEngagement.bankConf] : []);
        const updatedBankConfs = [...existingBankConfs, newBankConfValue];
        updateEngagement(selectedEngagement.id, 'bankConf', updatedBankConfs);
      }

      setShowConfirmation(true);
    } catch (error) {
      console.error('Error exporting document:', error);
      alert('导出失败：' + (error instanceof Error ? error.message : '未知错误'));
    }
  };

  // Handle partner selection
  const handlePartnerSelection = (engagementId: number, partner: string) => {
    updateEngagement(engagementId, 'partner', partner);
  };

  // Handle generate another
  const handleGenerateAnother = () => {
    setShowConfirmation(false);
    // Reset form for another generation
    setUseSystemData(null);
    setBankName('');
    setBankAddress('');
    setClientName('');
    setClientBankAccountNumber('');
  };

  // Handle close confirmation
  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setShowBankConfModal(false);
  };

  // Handle sorting
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to engagements
  const sortedEngagements = (engagements: any[]) => {
    if (!sortConfig) return engagements;

    return [...engagements].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special cases for sorting
      if (sortConfig.key === 'services') {
        aValue = Array.isArray(aValue) ? aValue.join(', ') : aValue;
        bValue = Array.isArray(bValue) ? bValue.join(', ') : bValue;
      }

      // Convert to string for comparison if not already
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Convert to string and handle numeric values
      const aStr = String(aValue);
      const bStr = String(bValue);

      // Check if both values are numeric
      const aNum = parseFloat(aStr);
      const bNum = parseFloat(bStr);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum < bNum) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aNum > bNum) return sortConfig.direction === 'asc' ? 1 : -1;
      } else {
        // String comparison
        const comparison = aStr.localeCompare(bStr);
        if (comparison !== 0) {
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
      }

      return 0;
    });
  };

  // Handle PDF upload modal
  const handlePdfUploadClick = (engagement: any) => {
    setCurrentEngagement(engagement);
    setShowPdfUploadModal(true);
  };

  // Handle PDF file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type === 'application/pdf') {
        setPdfFile(file);
      } else {
        alert('Please select a PDF file');
      }
    }
  };

  // Handle PDF upload
  const handleUpload = () => {
    if (currentEngagement && pdfFile) {
      // Update the status to "signed"
      updateEngagement(currentEngagement.id, 'status', 'signed');

      // Update the bankConf field to show "bank" instead of "prepared"
      if (currentEngagement.bankConf) {
        const bankConfs = Array.isArray(currentEngagement.bankConf) ? currentEngagement.bankConf : [currentEngagement.bankConf];
        const updatedBankConfs = bankConfs.map((conf: string) => conf.replace('prepared', 'bank'));
        updateEngagement(currentEngagement.id, 'bankConf', updatedBankConfs);
      }

      // Add the new document to the documents array
      const newDocName = `${pdfFile.name} - ${new Date().toISOString().split('T')[0]}`;
      const existingDocs = Array.isArray(currentEngagement.document) ? currentEngagement.document :
                          (currentEngagement.document && currentEngagement.document !== 'No' && currentEngagement.document !== 'TBD' ? [currentEngagement.document] : []);
      const updatedDocs = [...existingDocs, newDocName];
      updateEngagement(currentEngagement.id, 'document', updatedDocs);

      setShowPdfUploadModal(false);
      setPdfFile(null);
    }
  };

  // Handle Excel export
  const handleExportExcel = () => {
    let dataToExport: any[] = [];

    switch (activeTabForExport) {
      case 'audit':
        dataToExport = sortedAllAuditEngagements;
        break;
      case 'accounting':
        dataToExport = sortedAllAccountingEngagements;
        break;
      case 'secretarial':
        dataToExport = sortedAllCompanySecretarialEngagements;
        break;
      default:
        dataToExport = sortedEngagements(engagements);
    }

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${activeTabForExport.charAt(0).toUpperCase() + activeTabForExport.slice(1)} Engagements`);

    // Export the file
    XLSX.writeFile(wb, `${activeTabForExport}_engagements.xlsx`);
  };

  // Check if deadline is within 5 days
  const isDeadlineWithinFiveDays = (deadline: string): boolean => {
    if (!deadline || deadline === 'TBD') return false;

    try {
      const deadlineDate = parseISO(deadline);
      const today = new Date();
      const diffDays = differenceInDays(deadlineDate, today);
      return diffDays >= 0 && diffDays <= 5;
    } catch (error) {
      // If date parsing fails, return false
      return false;
    }
  };

  // Filter engagements for different service types
  const allAuditEngagements = engagements.filter(eng =>
    (eng.services.includes('audit') || eng.services.includes('account + audit')) &&
    (!show18MonthFilter ||
      (eng.yearEndDate &&
       new Date(eng.yearEndDate) >= new Date(new Date().setMonth(new Date().getMonth() - 18))
      )
    )
  );
  const sortedAllAuditEngagements = sortedEngagements(allAuditEngagements);
  const auditEngagements = sortedAllAuditEngagements.slice(
    (auditCurrentPage - 1) * itemsPerPage,
    auditCurrentPage * itemsPerPage
  );
  const totalAuditPages = Math.ceil(allAuditEngagements.length / itemsPerPage);

  const allAccountingEngagements = engagements.filter(eng =>
    eng.services.includes('account') || eng.services.includes('account only') || eng.services.includes('account + audit')
  );
  const sortedAllAccountingEngagements = sortedEngagements(allAccountingEngagements);
  const accountingEngagements = sortedAllAccountingEngagements.slice(
    (accountingCurrentPage - 1) * itemsPerPage,
    accountingCurrentPage * itemsPerPage
  );
  const totalAccountingPages = Math.ceil(allAccountingEngagements.length / itemsPerPage);

  // For Company Secretarial, we need to generate GCS codes
  const allCompanySecretarialEngagements = engagements
    .filter(eng =>
      eng.services.includes('company secretary') ||
      eng.services.some(service => [
        'Annual Company Secretary Services',
        'Registered Office Services',
        'Company Secretary Document Services',
        'De-registration Services',
        'BR fee paid on behalf of client',
        'Others'
      ].includes(service))
    )
    .map(eng => ({
      ...eng,
      engagementCode: eng.engagementCode.startsWith('AUD') ? `GCS${eng.engagementCode.substring(3)}` : eng.engagementCode
    }));
  const sortedAllCompanySecretarialEngagements = sortedEngagements(allCompanySecretarialEngagements);
  const companySecretarialEngagements = sortedAllCompanySecretarialEngagements.slice(
    (secretarialCurrentPage - 1) * itemsPerPage,
    secretarialCurrentPage * itemsPerPage
  );
  const totalSecretarialPages = Math.ceil(allCompanySecretarialEngagements.length / itemsPerPage);

  // Get the default active key from URL parameters
  const getDefaultActiveKey = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['audit', 'accounting', 'secretarial'].includes(tabParam)) {
      return tabParam;
    }
    return 'audit'; // default tab
  };

  const [activeKey, setActiveKey] = useState(getDefaultActiveKey);

  // Update active key when URL parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['audit', 'accounting', 'secretarial'].includes(tabParam)) {
      setActiveKey(tabParam);
    }
  }, [searchParams]);

  const { t } = useLanguage();

  return (
    <div className="engagement">
      <Container>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>{t('engagement')}</h2>
          <div className="d-flex gap-2">
            <Button
              variant={show18MonthFilter ? "success" : "outline-secondary"}
              onClick={handle18MonthFilter}
            >
              {show18MonthFilter ? 'All Projects' : 'Last 18 Months'} Audit Projects
            </Button>
            {/* <Button
              variant="primary"
              onClick={() => {
                setActiveTabForExport(activeKey);
                handleExportExcel();
              }}
            >
              Export to Excel
            </Button> */}
          </div>
        </div>
        <Tabs
          activeKey={activeKey}
          onSelect={(k) => {
            if (k) {
              setActiveKey(k);
              setActiveTabForExport(k);
            }
          }}
          id="engagement-tabs"
          className="mb-3"
        >
          {/* Audit Service Tab */}
          <Tab eventKey="audit" title={t('auditEngagement')}>
            <div style={{ padding: '15px' }}>
              <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('id')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      #
                      {sortConfig?.key === 'id' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(編號)</div>
                  </th>
                  <th
                    onClick={() => handleSort('engagementCode')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('Engagement Code')}
                      {sortConfig?.key === 'engagementCode' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(項目代碼)</div>
                  </th>
                  <th
                    onClick={() => handleSort('auditClientCode')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('auditClientCode')}
                      {sortConfig?.key === 'auditClientCode' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(審計客戶代碼)</div>
                  </th>
                  <th
                    onClick={() => handleSort('companyName')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('companyName')}
                      {sortConfig?.key === 'companyName' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(公司名稱)</div>
                  </th>
                  <th
                    onClick={() => handleSort('partner')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('partner')}
                      {sortConfig?.key === 'partner' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(合夥人)</div>
                  </th>
                  <th
                    onClick={() => handleSort('auditMgr')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Audit Mgr
                      {sortConfig?.key === 'auditMgr' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(審計經理)</div>
                  </th>
                  <th
                    onClick={() => handleSort('auditInCharge')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Audit in-charge
                      {sortConfig?.key === 'auditInCharge' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(審計負責人)</div>
                  </th>
                  <th
                    onClick={() => handleSort('yearEndDate')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Year end date
                      {sortConfig?.key === 'yearEndDate' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(年度結算日期)</div>
                  </th>
                  <th
                    onClick={() => handleSort('expectedDeadline')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Expected deadline
                      {sortConfig?.key === 'expectedDeadline' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(預期截止日期)</div>
                  </th>
                  <th
                    onClick={() => handleSort('bankConf')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Bank conf
                      {sortConfig?.key === 'bankConf' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(銀行確認書)</div>
                  </th>
                  <th
                    onClick={() => handleSort('taxReturns')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Tax returns
                      {sortConfig?.key === 'taxReturns' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(稅務申報)</div>
                  </th>
                  <th
                    onClick={() => handleSort('invoice')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('invoice')}
                      {sortConfig?.key === 'invoice' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(發票)</div>
                  </th>
                  <th
                    onClick={() => handleSort('receipt')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Receipt
                      {sortConfig?.key === 'receipt' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(收據)</div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Status
                      {sortConfig?.key === 'status' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(狀態)</div>
                  </th>
                  <th
                    onClick={() => handleSort('taxReturnsFiled')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Tax returns filed
                      {sortConfig?.key === 'taxReturnsFiled' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(已申報稅務)</div>
                  </th>
                  <th
                    onClick={() => handleSort('opinion')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Opinion
                      {sortConfig?.key === 'opinion' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(意見)</div>
                  </th>
                  <th
                    onClick={() => handleSort('typeOfEngagement')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Type of engagement
                      {sortConfig?.key === 'typeOfEngagement' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(業務類型)</div>
                  </th>
                  <th
                    onClick={() => handleSort('document')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Document
                      {sortConfig?.key === 'document' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(文件)</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {auditEngagements.map((engagement) => (
                  <tr key={engagement.id}>
                    <td>{engagement.id}</td>
                    <td
                      onClick={() => startEditing(engagement, 'engagementCode')}
                      style={{ cursor: 'pointer', color: isDeadlineWithinFiveDays(engagement.expectedDeadline) ? 'red' : 'inherit' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'engagementCode' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.engagementCode}
                            onChange={(e) => handleEditField(engagement.id, 'engagementCode', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.engagementCode
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'auditClientCode')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'auditClientCode' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.auditClientCode}
                            onChange={(e) => handleEditField(engagement.id, 'auditClientCode', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.auditClientCode
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'companyName')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'companyName' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.companyName}
                            onChange={(e) => handleEditField(engagement.id, 'companyName', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.companyName
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'partner')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'partner' ? (
                        <div className="editable-cell">
                          <Form.Select
                            value={engagement.partner}
                            onChange={(e) => handleEditField(engagement.id, 'partner', e.target.value)}
                            onBlur={stopEditing}
                            autoFocus
                          >
                            <option value="">Select Partner</option>
                            <option value="Jacky">Jacky</option>
                            <option value="Henry">Henry</option>
                            <option value="Kenneth">Kenneth</option>
                          </Form.Select>
                        </div>
                      ) : (
                        engagement.partner
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'auditMgr')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'auditMgr' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.auditMgr}
                            onChange={(e) => handleEditField(engagement.id, 'auditMgr', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.auditMgr
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'auditInCharge')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'auditInCharge' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.auditInCharge}
                            onChange={(e) => handleEditField(engagement.id, 'auditInCharge', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.auditInCharge
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'yearEndDate')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'yearEndDate' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="date"
                            value={engagement.yearEndDate}
                            onChange={(e) => handleEditField(engagement.id, 'yearEndDate', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.yearEndDate
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'expectedDeadline')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'expectedDeadline' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="date"
                            value={engagement.expectedDeadline}
                            onChange={(e) => handleEditField(engagement.id, 'expectedDeadline', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.expectedDeadline
                      )}
                    </td>
                    <td
                      onClick={() => handleBankConfClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {(() => {
                        const bankConfs = Array.isArray(engagement.bankConf) ? engagement.bankConf : [engagement.bankConf];
                        const hasValidBankConf = bankConfs.some((conf: string) =>
                          conf && conf.toLowerCase() !== 'no' && conf.toLowerCase() !== 'pending' && conf.toLowerCase() !== 'tbd'
                        );
                        
                        if (!hasValidBankConf) {
                          return 'No';
                        }
                        
                        return (
                          <div>
                            {bankConfs.map((conf: string, index: number) => {
                              if (!conf || conf.toLowerCase() === 'no' || conf.toLowerCase() === 'pending' || conf.toLowerCase() === 'tbd') {
                                return null;
                              }
                              
                              const displayConf = engagement.status === 'signed' && conf.includes('prepared')
                                ? conf.replace('prepared', 'bank')
                                : conf;
                              
                              return (
                                <div key={index} style={{ color: engagement.status === 'signed' && conf.includes('prepared') ? 'blue' : 'inherit' }}>
                                  {displayConf}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'taxReturns')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'taxReturns' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.taxReturns}
                            onChange={(e) => handleEditField(engagement.id, 'taxReturns', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.taxReturns
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'invoice')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'invoice' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.invoice}
                            onChange={(e) => handleEditField(engagement.id, 'invoice', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.invoice
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'receipt')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'receipt' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.receipt}
                            onChange={(e) => handleEditField(engagement.id, 'receipt', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.receipt
                      )}
                    </td>
                    <td
                      onClick={() => handleStatusClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {engagement.status}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'taxReturnsFiled')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'taxReturnsFiled' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.taxReturnsFiled}
                            onChange={(e) => handleEditField(engagement.id, 'taxReturnsFiled', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.taxReturnsFiled
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'opinion')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'opinion' ? (
                        <div className="editable-cell">
                          <Form.Select
                            value={engagement.opinion}
                            onChange={(e) => handleEditField(engagement.id, 'opinion', e.target.value)}
                            onBlur={stopEditing}
                            autoFocus
                          >
                            <option value="Clean">Clean</option>
                            <option value="Qualified">Qualified</option>
                            <option value="Disclaimer">Disclaimer</option>
                          </Form.Select>
                        </div>
                      ) : (
                        engagement.opinion
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'typeOfEngagement')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'typeOfEngagement' ? (
                        <div className="editable-cell">
                          <Form.Control
                            type="text"
                            value={engagement.typeOfEngagement}
                            onChange={(e) => handleEditField(engagement.id, 'typeOfEngagement', e.target.value)}
                            onBlur={stopEditing}
                            onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                            autoFocus
                          />
                        </div>
                      ) : (
                        engagement.typeOfEngagement
                      )}
                    </td>
                    <td
                      onClick={() => handlePdfUploadClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {(() => {
                        const docs = Array.isArray(engagement.document) ? engagement.document : [engagement.document];
                        return docs.length > 0 && docs[0] !== 'No' && docs[0] !== 'TBD' ? (
                          <div>
                            {docs.map((doc: string, index: number) => (
                              <div key={index}>
                                <a href="/blank.pdf" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                  <span style={{ color: 'red', fontSize: '1.2em', marginRight: '5px' }}>📄</span>
                                </a>
                                {doc}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                            {docs[0] || 'No'}
                            <span style={{ marginLeft: '5px', fontSize: '0.8em', opacity: '0.7' }}>↑</span>
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination Controls for Audit Tab */}
            <div className="d-flex justify-content-center mt-3">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${auditCurrentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setAuditCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={auditCurrentPage === 1}
                    >
                      {t('previous')}
                    </button>
                  </li>

                  {/* Show page numbers */}
                  {Array.from({ length: totalAuditPages }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${auditCurrentPage === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setAuditCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className={`page-item ${auditCurrentPage === totalAuditPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setAuditCurrentPage(prev => Math.min(prev + 1, totalAuditPages))}
                      disabled={auditCurrentPage === totalAuditPages}
                    >
                      {t('next')}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
            </div>
          </Tab>

          {/* Accounting Service Tab */}
          <Tab eventKey="accounting" title={t('accountOnlyEngagement')}>
            <div style={{ padding: '15px' }}>
              <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('id')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      #
                      {sortConfig?.key === 'id' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(編號)</div>
                  </th>
                  <th
                    onClick={() => handleSort('engagementCode')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('Engagement Code')}
                      {sortConfig?.key === 'engagementCode' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(項目代碼)</div>
                  </th>
                  <th
                    onClick={() => handleSort('auditClientCode')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('auditClientCode')}
                      {sortConfig?.key === 'auditClientCode' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(審計客戶代碼)</div>
                  </th>
                  <th
                    onClick={() => handleSort('companyName')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('companyName')}
                      {sortConfig?.key === 'companyName' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(公司名稱)</div>
                  </th>
                  <th
                    onClick={() => handleSort('partner')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('partner')}
                      {sortConfig?.key === 'partner' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(合夥人)</div>
                  </th>
                  <th
                    onClick={() => handleSort('accountInCharge')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Account-in-charge
                      {sortConfig?.key === 'accountInCharge' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(會計負責人)</div>
                  </th>
                  <th
                    onClick={() => handleSort('auditInCharge')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Audit in-charge
                      {sortConfig?.key === 'auditInCharge' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(審計負責人)</div>
                  </th>
                  <th
                    onClick={() => handleSort('yearEndDate')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Year end date
                      {sortConfig?.key === 'yearEndDate' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(年度結算日期)</div>
                  </th>
                  <th
                    onClick={() => handleSort('expectedDeadline')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Expected deadline
                      {sortConfig?.key === 'expectedDeadline' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(預期截止日期)</div>
                  </th>
                  <th
                    onClick={() => handleSort('bankConf')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Bank conf
                      {sortConfig?.key === 'bankConf' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(銀行確認書)</div>
                  </th>
                  <th
                    onClick={() => handleSort('taxReturns')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Tax returns
                      {sortConfig?.key === 'taxReturns' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(稅務申報)</div>
                  </th>
                  <th
                    onClick={() => handleSort('invoice')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('invoice')}
                      {sortConfig?.key === 'invoice' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(發票)</div>
                  </th>
                  <th
                    onClick={() => handleSort('receipt')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Receipt
                      {sortConfig?.key === 'receipt' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(收據)</div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Status
                      {sortConfig?.key === 'status' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(狀態)</div>
                  </th>
                  <th
                    onClick={() => handleSort('document')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Document
                      {sortConfig?.key === 'document' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(文件)</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {accountingEngagements.map((engagement) => (
                  <tr key={engagement.id}>
                    <td>{engagement.id}</td>
                    <td
                      onClick={() => startEditing(engagement, 'engagementCode')}
                      style={{ cursor: 'pointer', color: isDeadlineWithinFiveDays(engagement.expectedDeadline) ? 'red' : 'inherit' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'engagementCode' ? (
                        <Form.Control
                          type="text"
                          value={engagement.engagementCode}
                          onChange={(e) => handleEditField(engagement.id, 'engagementCode', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.engagementCode
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'auditClientCode')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'auditClientCode' ? (
                        <Form.Control
                          type="text"
                          value={engagement.auditClientCode}
                          onChange={(e) => handleEditField(engagement.id, 'auditClientCode', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.auditClientCode
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'companyName')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'companyName' ? (
                        <Form.Control
                          type="text"
                          value={engagement.companyName}
                          onChange={(e) => handleEditField(engagement.id, 'companyName', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.companyName
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'partner')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'partner' ? (
                        <Form.Select
                          value={engagement.partner}
                          onChange={(e) => handleEditField(engagement.id, 'partner', e.target.value)}
                          onBlur={stopEditing}
                          autoFocus
                        >
                          <option value="">Select Partner</option>
                          <option value="Jacky">Jacky</option>
                          <option value="Henry">Henry</option>
                          <option value="Kenneth">Kenneth</option>
                        </Form.Select>
                      ) : (
                        engagement.partner
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'accountInCharge')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'accountInCharge' ? (
                        <Form.Control
                          type="text"
                          value={engagement.accountInCharge}
                          onChange={(e) => handleEditField(engagement.id, 'accountInCharge', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.accountInCharge
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'auditInCharge')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'auditInCharge' ? (
                        <Form.Control
                          type="text"
                          value={engagement.auditInCharge}
                          onChange={(e) => handleEditField(engagement.id, 'auditInCharge', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.auditInCharge
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'yearEndDate')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'yearEndDate' ? (
                        <Form.Control
                          type="date"
                          value={engagement.yearEndDate}
                          onChange={(e) => handleEditField(engagement.id, 'yearEndDate', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.yearEndDate
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'expectedDeadline')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'expectedDeadline' ? (
                        <Form.Control
                          type="date"
                          value={engagement.expectedDeadline}
                          onChange={(e) => handleEditField(engagement.id, 'expectedDeadline', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.expectedDeadline
                      )}
                    </td>
                    <td
                      onClick={() => handleBankConfClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {(() => {
                        const bankConfs = Array.isArray(engagement.bankConf) ? engagement.bankConf : [engagement.bankConf];
                        const hasValidBankConf = bankConfs.some((conf: string) =>
                          conf && conf.toLowerCase() !== 'no' && conf.toLowerCase() !== 'pending' && conf.toLowerCase() !== 'tbd'
                        );
                        
                        if (!hasValidBankConf) {
                          return 'No';
                        }
                        
                        return (
                          <div>
                            {bankConfs.map((conf: string, index: number) => {
                              if (!conf || conf.toLowerCase() === 'no' || conf.toLowerCase() === 'pending' || conf.toLowerCase() === 'tbd') {
                                return null;
                              }
                              
                              const displayConf = engagement.status === 'signed' && conf.includes('prepared')
                                ? conf.replace('prepared', 'bank')
                                : conf;
                              
                              return (
                                <div key={index} style={{ color: engagement.status === 'signed' && conf.includes('prepared') ? 'blue' : 'inherit' }}>
                                  {displayConf}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'taxReturns')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'taxReturns' ? (
                        <Form.Control
                          type="text"
                          value={engagement.taxReturns}
                          onChange={(e) => handleEditField(engagement.id, 'taxReturns', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.taxReturns
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'invoice')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'invoice' ? (
                        <Form.Control
                          type="text"
                          value={engagement.invoice}
                          onChange={(e) => handleEditField(engagement.id, 'invoice', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.invoice
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'receipt')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'receipt' ? (
                        <Form.Control
                          type="text"
                          value={engagement.receipt}
                          onChange={(e) => handleEditField(engagement.id, 'receipt', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.receipt
                      )}
                    </td>
                    <td
                      onClick={() => handleStatusClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {engagement.status}
                    </td>
                    <td
                      onClick={() => handlePdfUploadClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {(() => {
                        const docs = Array.isArray(engagement.document) ? engagement.document : [engagement.document];
                        return docs.length > 0 && docs[0] !== 'No' && docs[0] !== 'TBD' ? (
                          <div>
                            {docs.map((doc: string, index: number) => (
                              <div key={index}>
                                <a href="/blank.pdf" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                  <span style={{ color: 'red', fontSize: '1.2em', marginRight: '5px' }}>📄</span>
                                </a>
                                {doc}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                            {docs[0] || 'No'}
                            <span style={{ marginLeft: '5px', fontSize: '0.8em', opacity: '0.7' }}>↑</span>
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination Controls for Accounting Tab */}
            <div className="d-flex justify-content-center mt-3">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${accountingCurrentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setAccountingCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={accountingCurrentPage === 1}
                    >
                      {t('previous')}
                    </button>
                  </li>

                  {/* Show page numbers */}
                  {Array.from({ length: totalAccountingPages }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${accountingCurrentPage === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setAccountingCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className={`page-item ${accountingCurrentPage === totalAccountingPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setAccountingCurrentPage(prev => Math.min(prev + 1, totalAccountingPages))}
                      disabled={accountingCurrentPage === totalAccountingPages}
                    >
                      {t('next')}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
            </div>
          </Tab>

          {/* Company Secretarial Tab */}
          <Tab eventKey="secretarial" title={t('companySecretaryEngagement')}>
            <div style={{ padding: '15px' }}>
              <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('id')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      #
                      {sortConfig?.key === 'id' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(編號)</div>
                  </th>
                  <th
                    onClick={() => handleSort('engagementCode')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('Engagement Code')}
                      {sortConfig?.key === 'engagementCode' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(項目代碼)</div>
                  </th>
                  <th
                    onClick={() => handleSort('companySecretarialCode')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('companySecretarialClientCode')}
                      {sortConfig?.key === 'companySecretarialCode' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(公司秘書客戶代碼)</div>
                  </th>
                  <th
                    onClick={() => handleSort('companyName')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('companyName')}
                      {sortConfig?.key === 'companyName' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(公司名稱)</div>
                  </th>
                  <th
                    onClick={() => handleSort('partner')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('partner')}
                      {sortConfig?.key === 'partner' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(合夥人)</div>
                  </th>
                  <th
                    onClick={() => handleSort('services')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('services')}
                      {sortConfig?.key === 'services' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(服務)</div>
                  </th>
                  <th
                    onClick={() => handleSort('expectedDeadline')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Expected deadline
                      {sortConfig?.key === 'expectedDeadline' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(預期截止日期)</div>
                  </th>
                  <th
                    onClick={() => handleSort('invoice')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      {t('invoice')}
                      {sortConfig?.key === 'invoice' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(發票)</div>
                  </th>
                  <th
                    onClick={() => handleSort('receipt')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Receipt
                      {sortConfig?.key === 'receipt' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(收據)</div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      Status
                      {sortConfig?.key === 'status' && (
                        <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                      )}
                    </div>
                    <div className="text-muted small">(狀態)</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {companySecretarialEngagements.map((engagement) => (
                  <tr key={engagement.id}>
                    <td>{engagement.id}</td>
                    <td
                      onClick={() => startEditing(engagement, 'engagementCode')}
                      style={{ cursor: 'pointer', color: isDeadlineWithinFiveDays(engagement.expectedDeadline) ? 'red' : 'inherit' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'engagementCode' ? (
                        <Form.Control
                          type="text"
                          value={engagement.engagementCode}
                          onChange={(e) => handleEditField(engagement.id, 'engagementCode', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.engagementCode
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'companySecretarialCode')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'companySecretarialCode' ? (
                        <Form.Control
                          type="text"
                          value={engagement.companySecretarialCode}
                          onChange={(e) => handleEditField(engagement.id, 'companySecretarialCode', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.companySecretarialCode
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'companyName')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'companyName' ? (
                        <Form.Control
                          type="text"
                          value={engagement.companyName}
                          onChange={(e) => handleEditField(engagement.id, 'companyName', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.companyName
                      )}
                    </td>
                    <td
                      onClick={() => startEditing(engagement, 'partner')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'partner' ? (
                        <Form.Select
                          value={engagement.partner}
                          onChange={(e) => handleEditField(engagement.id, 'partner', e.target.value)}
                          onBlur={stopEditing}
                          autoFocus
                        >
                          <option value="">Select Partner</option>
                          <option value="Jacky">Jacky</option>
                          <option value="Henry">Henry</option>
                          <option value="Kenneth">Kenneth</option>
                        </Form.Select>
                      ) : (
                        engagement.partner
                      )}
                    </td>
                    <td
                      onClick={() => handleServicesClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {engagement.services.join(', ')}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'expectedDeadline')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'expectedDeadline' ? (
                        <Form.Control
                          type="date"
                          value={engagement.expectedDeadline}
                          onChange={(e) => handleEditField(engagement.id, 'expectedDeadline', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.expectedDeadline
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'invoice')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'invoice' ? (
                        <Form.Control
                          type="text"
                          value={engagement.invoice}
                          onChange={(e) => handleEditField(engagement.id, 'invoice', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.invoice
                      )}
                    </td>
                    <td 
                      onClick={() => startEditing(engagement, 'receipt')}
                      style={{ cursor: 'pointer' }}
                    >
                      {editingEngagement?.id === engagement.id && editingField === 'receipt' ? (
                        <Form.Control
                          type="text"
                          value={engagement.receipt}
                          onChange={(e) => handleEditField(engagement.id, 'receipt', e.target.value)}
                          onBlur={stopEditing}
                          onKeyDown={(e) => e.key === 'Enter' && stopEditing()}
                          autoFocus
                        />
                      ) : (
                        engagement.receipt
                      )}
                    </td>
                    <td
                      onClick={() => handleStatusClick(engagement)}
                      style={{ cursor: 'pointer' }}
                    >
                      {engagement.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination Controls for Secretarial Tab */}
            <div className="d-flex justify-content-center mt-3">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${secretarialCurrentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setSecretarialCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={secretarialCurrentPage === 1}
                    >
                      {t('previous')}
                    </button>
                  </li>

                  {/* Show page numbers */}
                  {Array.from({ length: totalSecretarialPages }, (_, i) => i + 1).map(page => (
                    <li key={page} className={`page-item ${secretarialCurrentPage === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setSecretarialCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className={`page-item ${secretarialCurrentPage === totalSecretarialPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setSecretarialCurrentPage(prev => Math.min(prev + 1, totalSecretarialPages))}
                      disabled={secretarialCurrentPage === totalSecretarialPages}
                    >
                      {t('next')}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
            </div>
          </Tab>
        </Tabs>
      </Container>

      {/* Bank Confirmation Modal */}
      <Modal show={showBankConfModal} onHide={() => setShowBankConfModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bank Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {useSystemData === null ? (
            <div>
              <h5>Need to generate bank conf from system?</h5>
              <div className="d-flex justify-content-center mt-4">
                <Button variant="success" onClick={handleYesClick} className="me-3">
                  Yes
                </Button>
                <Button variant="secondary" onClick={handleNoClick}>
                  No
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Bank Name</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <InputGroup>
                      <FormControl
                        type="text"
                        value={searchQuery || bankName}
                        onChange={handleSearchChange}
                        placeholder="Search bank by name, code, or branch..."
                      />
                      <Button variant="outline-secondary" id="button-search">
                        <i className="bi bi-search"></i>
                      </Button>
                    </InputGroup>

                    {showSearchResults && searchQuery && (
                      <div className="search-results mt-1">
                        {isSearching ? (
                          <div className="p-2 text-center text-muted">
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="ms-2">Searching banks...</span>
                          </div>
                        ) : searchResults.length > 0 ? (
                          searchResults.map((bank, index) => (
                            <div
                              key={index}
                              className="p-2 border-bottom cursor-pointer hover-bg"
                              onClick={() => handleBankSelect(bank)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div><strong>{bank['Institution Name']}</strong> ({bank['ABBR']})</div>
                              <div className="text-muted">Clearing Code: {bank['Clearing Code'].toString().padStart(3, '0')} | Branch: {bank['Branch Name']} ({bank['Branch Code'].toString().padStart(3, '0')})</div>
                              <div className="text-muted small">{bank['Branch Address']}</div>
                            </div>
                          ))
                        ) : (
                          <div className="p-2 text-muted">
                            No banks found matching "{searchQuery}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Bank Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={bankAddress}
                    onChange={(e) => setBankAddress(e.target.value)}
                    placeholder="Enter bank address"
                  />
                </Form.Group>

                {useSystemData && (
                  <Form.Group className="mb-3">
                    <Form.Label>Client Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Enter client name"
                    />
                  </Form.Group>
                )}

                {useSystemData && (
                  <Form.Group className="mb-3">
                    <Form.Label>Client Bank Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={clientBankAccountNumber}
                      onChange={(e) => setClientBankAccountNumber(e.target.value)}
                      placeholder="Enter client bank account number"
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Year End Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={yearEndDate}
                    onChange={(e) => setYearEndDate(e.target.value)}
                    placeholder="Enter year end date"
                  />
                </Form.Group>
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {useSystemData !== null && (
            <>
              <Button variant="primary" onClick={handlePreviewClick}>
                Preview
              </Button>
              <Button variant="secondary" onClick={() => setShowBankConfModal(false)}>
                Cancel
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Bank Confirmation Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isGeneratingPreview && (
            <div style={{ 
              textAlign: 'center', 
              padding: '50px',
              minHeight: '600px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '20px', fontSize: '18px', color: '#666' }}>正在生成preview...</p>
            </div>
          )}
          <div 
            ref={previewContainerRef} 
            style={{ 
              minHeight: '600px', 
              maxHeight: '800px', 
              overflow: 'auto',
              border: '1px solid #ddd',
              padding: '20px',
              backgroundColor: '#f5f5f5',
              display: isGeneratingPreview ? 'none' : 'block'
            }}
          />
          {previewError && (
            <div style={{ 
              marginTop: '20px', 
              padding: '15px', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24'
            }}>
              <strong>错误：</strong> {previewError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="success" 
            onClick={handleExportWord}
            disabled={isGeneratingPreview}
          >
            {isGeneratingPreview ? '生成中...' : 'Export as Word'}
          </Button>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmation} onHide={handleCloseConfirmation}>
        <Modal.Header closeButton>
          <Modal.Title>Generate Another Bank Conf?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Would you like to generate another bank confirmation?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleGenerateAnother}>
            Yes
          </Button>
          <Button variant="secondary" onClick={handleCloseConfirmation}>
            No
          </Button>
        </Modal.Footer>
      </Modal>

      {/* PDF Upload Modal */}
      <Modal show={showPdfUploadModal} onHide={() => setShowPdfUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Upload PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please upload a PDF file for the engagement status.</p>
          <Form.Group>
            <Form.Label>Select PDF File</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPdfUploadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!pdfFile}
          >
            Upload
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Status Modal */}
      <Modal show={showStatusModal} onHide={handleCancelStatus} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>狀態詳情<br /><small className="text-muted">(Status Details)</small></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentStatusEngagement && (
            <div>
              <h5>項目詳情<br /><small className="text-muted">(Engagement Details)</small></h5>
              <p><strong>項目代碼<br /><small className="text-muted">(Engagement Code)</small>:</strong> {currentStatusEngagement.engagementCode}</p>
              <p><strong>公司<br /><small className="text-muted">(Company)</small>:</strong> {currentStatusEngagement.companyName}</p>
              <p><strong>合夥人<br /><small className="text-muted">(Partner)</small>:</strong> {currentStatusEngagement.partner}</p>

              <Form.Group className="mb-3">
                <Form.Label>設定狀態<br /><small className="text-muted">(Set Status)</small></Form.Label>
                <Form.Select
                  value={currentStatus}
                  onChange={handleStatusChange}
                >
                  {currentStatusEngagement.engagementCode.startsWith('AUD') ? (
                    <>
                      <option value="Audit-in-charge start">Audit-in-charge start (審計負責人開始)</option>
                      <option value="Manager review">Manager review (經理審核)</option>
                      <option value="Partner review">Partner review (合夥人審核)</option>
                      <option value="Draft report to client">Draft report to client (草擬報告給客戶)</option>
                      <option value="Original report to client">Original report to client (正式報告給客戶)</option>
                      <option value="Audit Report Date">Audit Report Date (審計報告日期)</option>
                    </>
                  ) : currentStatusEngagement.engagementCode.startsWith('ACC') ? (
                    <>
                      <option value="document received">document received (收到文件)</option>
                      <option value="Acct start">Acct start (會計開始)</option>
                      <option value="Send to client review">Send to client review (發送給客戶審核)</option>
                      <option value="To audit kick off">To audit kick off (移交審計啟動)</option>
                    </>
                  ) : (
                    <>
                      <option value="document preparing">document preparing (文件準備中)</option>
                      <option value="document sent to client to sign">document sent to client to sign (文件發送給客戶簽署)</option>
                      <option value="document filed to CR">document filed to CR (文件存檔至公司註冊處)</option>
                      <option value="Others">Others (其他)</option>
                    </>
                  )}
                </Form.Select>
              </Form.Group>

              {currentStatus === 'Others' && (
                <Form.Group className="mb-3">
                  <Form.Label>指定狀態<br /><small className="text-muted">(Specify Status)</small></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter custom status"
                    value={currentStatus}
                    onChange={(e) => setCurrentStatus(e.target.value)}
                  />
                </Form.Group>
              )}

              {/* Progress percentage calculation */}
              <div className="mb-3 p-3 bg-light rounded">
                <h6>項目進度<br /><small className="text-muted">(Progress)</small></h6>
                <div className="progress" style={{ height: '20px' }}>
                  {(() => {
                    // Determine total number of statuses based on engagement type
                    let totalStatuses = 0;
                    if (currentStatusEngagement?.engagementCode?.startsWith('AUD')) {
                      totalStatuses = 6; // Audit: Audit-in-charge start, Manager review, Partner review, Draft report to client, Original report to client, Audit Report Date
                    } else if (currentStatusEngagement?.engagementCode?.startsWith('ACC')) {
                      totalStatuses = 4; // Account: document received, Acct start, Send to client review, To audit kick off
                    } else {
                      totalStatuses = 4; // Company Secretary: document preparing, document sent to client to sign, document filed to CR, Others
                    }

                    // Calculate progress percentage
                    const completedStatuses = statusHistory.length;
                    const progressPercentage = totalStatuses > 0 ? Math.min(100, Math.round((completedStatuses / totalStatuses) * 100)) : 0;

                    return (
                      <>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${progressPercentage}%` }}
                          aria-valuenow={progressPercentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {progressPercentage}%
                        </div>
                      </>
                    );
                  })()}
                </div>
                <small className="text-muted">
                  已完成 {statusHistory.length} / {(() => {
                    if (currentStatusEngagement?.engagementCode?.startsWith('AUD')) return 6;
                    if (currentStatusEngagement?.engagementCode?.startsWith('ACC')) return 4;
                    return 4; // Company Secretary
                  })()} 個狀態<br />
                  <small>(Completed {statusHistory.length} / {(() => {
                    if (currentStatusEngagement?.engagementCode?.startsWith('AUD')) return 6;
                    if (currentStatusEngagement?.engagementCode?.startsWith('ACC')) return 4;
                    return 4; // Company Secretary
                  })()} statuses)</small>
                </small>
              </div>

              <h5 className="mt-4">狀態歷史<br /><small className="text-muted">(Status History)</small></h5>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>日期<br /><small className="text-muted">(Date)</small></th>
                    <th>狀態<br /><small className="text-muted">(Status)</small></th>
                    <th>批准人<br /><small className="text-muted">(Approved By)</small></th>
                  </tr>
                </thead>
                <tbody>
                  {statusHistory.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.date}</td>
                      <td>{entry.status}</td>
                      <td>{entry.approvedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelStatus}>
            取消<br /><small className="text-muted">(Cancel)</small>
          </Button>
          <Button variant="primary" onClick={handleSaveStatus}>
            保存<br /><small className="text-muted">(Save)</small>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Services Modal */}
      <Modal show={showServicesModal} onHide={handleCancelServices} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>服務選擇<br /><small className="text-muted">(Service Selection)</small></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentServicesEngagement && (
            <div>
              <h5>項目詳情<br /><small className="text-muted">(Engagement Details)</small></h5>
              <p><strong>項目代碼<br /><small className="text-muted">(Engagement Code)</small>:</strong> {currentServicesEngagement.engagementCode}</p>
              <p><strong>公司<br /><small className="text-muted">(Company)</small>:</strong> {currentServicesEngagement.companyName}</p>

              <Form.Group className="mt-3">
                <Form.Label>選擇服務<br /><small className="text-muted">(Select Services)</small></Form.Label>
                <div className="border p-3 rounded">
                  {[
                    { value: 'Annual Company Secretary Services', label: 'Annual Company Secretary Services (年度公司秘書服務)' },
                    { value: 'Registered Office Services', label: 'Registered Office Services (註冊辦事處服務)' },
                    { value: 'Company Secretary Document Services', label: 'Company Secretary Document Services (公司秘書文件服務)' },
                    { value: 'De-registration Services', label: 'De-registration Services (取消註冊服務)' },
                    { value: 'BR fee paid on behalf of client', label: 'BR fee paid on behalf of client (代客戶支付商業登記費)' },
                    { value: 'Others', label: 'Others (其他)' }
                  ].map((item, index) => (
                    <div key={index} className="mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`service-${index}`}
                        label={item.label}
                        checked={selectedServices.includes(item.value)}
                        onChange={() => handleServiceSelection(item.value)}
                      />
                      {item.value === 'Others' && selectedServices.includes('Others') && (
                        <Form.Control
                          type="text"
                          placeholder="請輸入其他服務 (Please specify other services)"
                          value={selectedServices.filter((s: string) => ![
                            'Annual Company Secretary Services',
                            'Registered Office Services',
                            'Company Secretary Document Services',
                            'De-registration Services',
                            'BR fee paid on behalf of client',
                            'Others'
                          ].includes(s)).join(', ') || ''}
                          onChange={(e) => {
                            // Remove any non-standard services and add the new value
                            let updatedServices = selectedServices.filter((s: string) => [
                              'Annual Company Secretary Services',
                              'Registered Office Services',
                              'Company Secretary Document Services',
                              'De-registration Services',
                              'BR fee paid on behalf of client',
                              'Others'
                            ].includes(s));

                            if (e.target.value.trim()) {
                              updatedServices = [...updatedServices, e.target.value.trim()];
                            }

                            setSelectedServices(updatedServices);
                          }}
                          className="ms-4 mt-1"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelServices}>
            取消<br /><small className="text-muted">(Cancel)</small>
          </Button>
          <Button variant="primary" onClick={handleSaveServices}>
            保存<br /><small className="text-muted">(Save)</small>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Engagement;